import { FC, useCallback, useMemo, useState } from 'react';
import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button from 'src/components/design-system/Button';
import PopUp from 'src/components/design-system/PopUp';
import SidePanel from 'src/components/design-system/SidePanel';
import useBoolean from 'src/hooks/useBoolean';
import useViewDepartment from 'src/hooks/useViewDepartments';
import useBulkUpdateMemberApi from 'src/services/useBulkUpdateMemberApi';
import { Department } from 'src/services/useGetDepartmentApi';
import 'src/style/grid/grid-override.scss';
import { findCommonMembers, mergeMembersArrays } from 'src/utils/transformation';
import DepartmentsTabs from '../DepartmentsTabs';
import { initialDepartmentState } from '../ViewDepartmentContext';
import AssignMemberTable, { IAssignMember } from './AssignMemberTable';
import MultiSelectDepartment from './MultiSelectDepartment';
import UnassignMemberTable from './UnassignMemberTable';

interface BulkUpdateMemberProps {}
const Tabs = ['Assign', 'Unassign'];

const BulkUpdateMember: FC<BulkUpdateMemberProps> = () => {
  const {
    t,
    unDeletedDepartmentList,
    clients,
    selectedRows,
    setSelectedRows,
    setDepartmentsDataState,
    selectedCompany,
  } = useViewDepartment();
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const { value: isPaneOpen, setTrue: openPane, setFalse: closePane } = useBoolean(false);
  const [assignMember, setAssignMember] = useState<IAssignMember[]>([]);
  const [unAssignMember, setUnAssignMember] = useState<IAssignMember[]>([]);
  const { bulkUpdateMember, isBulkUpdateMemberLoading } = useBulkUpdateMemberApi(
    selectedCompany.companyId
  );
  const { isBulkUpdateMemberDisable, message } = useMemo(() => {
    const isBulkUpdateMemberDisable = selectedRows.length < 1;
    let message = '';
    if (isBulkUpdateMemberDisable) {
      message = 'Please select Department(s) to assign Members';
    }
    return {
      isBulkUpdateMemberDisable,
      message,
    };
  }, [selectedRows.length]);

  const handleSelectTab = useCallback((num: number) => {
    setSelectedTab(num);
  }, []);

  const handlePaneClose = useCallback(() => {
    if (!isBulkUpdateMemberLoading) {
      closePane();
      setSelectedTab(0);
    }
  }, [closePane, isBulkUpdateMemberLoading]);

  const setInitialMembers = useCallback(
    (selectedRows: Department[]) => {
      const mergedMembers = mergeMembersArrays(selectedRows);
      const commonMembersOfDepartment = findCommonMembers(selectedRows);

      const initialUnassignMembers = clients?.filter((client) =>
        mergedMembers?.some((member) => member.id === client?.clientId)
      );

      const initialAssignMembers = clients?.filter(
        (client) => !commonMembersOfDepartment.some((member) => member.id === client?.clientId)
      );
      setAssignMember(initialAssignMembers?.map((members) => ({ ...members, selected: false })));
      setUnAssignMember(
        initialUnassignMembers?.map((members) => ({ ...members, selected: false }))
      );
    },
    [clients]
  );

  const handleMemberUpdateClick = useCallback(() => {
    openPane();
    setInitialMembers(selectedRows);
    setDepartmentsDataState(initialDepartmentState);
  }, [openPane, selectedRows, setDepartmentsDataState, setInitialMembers]);

  const handleDepartments = useCallback(
    (departments: Department[]) => {
      setSelectedRows(departments);
      setInitialMembers(departments);
    },
    [setInitialMembers, setSelectedRows]
  );

  return (
    <>
      <PopUp
        popupAlign={{ horizontal: 'center', vertical: 'top' }}
        content={
          <div className="m-1 max-w-[350px]">
            <PopUpContentWrapper type={'Information'} contents={[message]} />
          </div>
        }
        show={isBulkUpdateMemberDisable}
      >
        <Button
          color="primary"
          onClick={handleMemberUpdateClick}
          disabled={isBulkUpdateMemberDisable}
        >
          {t('Bulk Member Update')}
        </Button>
      </PopUp>

      <SidePanel
        isOpen={isPaneOpen}
        onClose={handlePaneClose}
        position="right"
        title="Bulk Update Members"
        className="bg-[#EEEEEE]"
        width="750px"
      >
        <MultiSelectDepartment
          departmentList={unDeletedDepartmentList}
          selectedDepartments={selectedRows}
          setSelectedDepartments={handleDepartments}
        />
        {Tabs[selectedTab] === 'Assign' ? (
          <p className="mt-4 mb-4 text-sm">
            Note: Members displayed below are only those eligible for assignment to one or more of
            the selected departments above. Members already assigned to all selected departments
            will not be displayed. All selected members will be assigned to all selected
            departments.
          </p>
        ) : (
          <p className="mt-4 mb-4 text-sm">
            Note: Members displayed below are only those eligible for unassignment from one or more
            of the selected departments above. Members already unassigned to all selected
            departments will not be displayed. All selected members will be unassigned from all
            departments.
          </p>
        )}
        <DepartmentsTabs Tabs={Tabs} handleSelectTab={handleSelectTab} selectedTab={selectedTab} />
        {Tabs[selectedTab] === 'Assign' ? (
          <AssignMemberTable
            handlePaneClose={handlePaneClose}
            selectedDepartments={selectedRows}
            assignMember={assignMember}
            setAssignMember={setAssignMember}
            bulkUpdateMember={bulkUpdateMember}
            isBulkUpdateMemberLoading={isBulkUpdateMemberLoading}
          />
        ) : (
          <UnassignMemberTable
            handlePaneClose={handlePaneClose}
            selectedDepartments={selectedRows}
            unAssignMember={unAssignMember}
            setUnAssignMember={setUnAssignMember}
            bulkUpdateMember={bulkUpdateMember}
            isBulkUpdateMemberLoading={isBulkUpdateMemberLoading}
          />
        )}
      </SidePanel>
    </>
  );
};

export default BulkUpdateMember;
